import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from '@/i18n/LanguageContext';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import CourseOverview from './components/CourseOverview';
import TargetAudience from './components/TargetAudience';
import LearningContent from './components/LearningContent';
import LearningProgression from './components/LearningProgression';
import DailySchedule from './components/DailySchedule';
import MasterPrograms from './components/MasterPrograms';
import CertificationSection from './components/CertificationSection';
import CourseBenefits from './components/CourseBenefits';
import NextStep from './components/NextStep';
import OverseasComparison from './components/OverseasComparison';
import AdmissionFlow from './components/AdmissionFlow';
import CTASection from './components/CTASection';
import RelatedPages from './components/RelatedPages';
import FAQSection from './components/FAQSection';
import Seo from '@/components/seo/Seo';

function splitParagraphs(text: string): string[] {
  return text.split(/\n{2,}/);
}

export default function MasterCoursePage() {
  const { t } = useTranslation();
  const { lang } = useLanguageContext();
  const isEn = lang === 'en';
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const breadcrumbData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: String(t('coursesMaster.jsonld.breadcrumb.home')).replace(/\n+/g, ' '),
        item: 'https://butler-academy.jp/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: String(t('coursesMaster.jsonld.breadcrumb.courses')).replace(/\n+/g, ' '),
        item: 'https://butler-academy.jp/courses',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: String(t('coursesMaster.jsonld.breadcrumb.current')).replace(/\n+/g, ' '),
        item: 'https://butler-academy.jp/courses/master',
      },
    ],
  };

  const courseData = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: String(t('coursesMaster.jsonld.course.name')).replace(/\n+/g, ' '),
    description: String(t('coursesMaster.jsonld.course.description')).replace(/\n+/g, ' '),
    provider: {
      '@type': 'EducationalOrganization',
      name: String(t('coursesMaster.jsonld.provider')).replace(/\n+/g, ' '),
    },
    offers: {
      '@type': 'Offer',
      price: '1680000',
      priceCurrency: 'JPY',
      category: 'Paid',
    },
    timeRequired: 'P3M',
  };

  return (
    <>
      <Seo route="/courses/master" />
      {/* Structured Data: BreadcrumbList */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbData) }}
      />

      {/* Structured Data: Course */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(courseData) }}
      />

      <Header />

      <main>
        <HeroSection />
        <CourseOverview />
        <TargetAudience />
        <LearningContent />
        <LearningProgression />
        <DailySchedule />
        <MasterPrograms />
        <CertificationSection />
        <CourseBenefits />
        <NextStep />
        <OverseasComparison />
        <AdmissionFlow />
        <CTASection />
        <RelatedPages />
        <FAQSection />

        {/* Last Updated */}
        <section className="py-8 bg-background-50">
          <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16 text-center">
            {splitParagraphs(String(t('coursesMaster.lastUpdated'))).map((paragraph, index) => (
              <p key={index} className="text-[13px] text-foreground-500 tracking-[0.05em]">
                {paragraph}
              </p>
            ))}
          </div>
        </section>
      </main>

      <Footer />

      {/* Back to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 z-40 w-11 h-11 rounded-full border border-accent-500/40 bg-primary-900/80 backdrop-blur-sm text-accent-400 hover:text-accent-300 hover:border-accent-400 hover:bg-primary-900 transition-all duration-300 flex items-center justify-center ${
          showBackToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        aria-label={t('coursesMaster.backToTop')}
      >
        <i className="ri-arrow-up-line text-lg"></i>
      </button>
    </>
  );
}
